import React, { useState } from 'react';
import { Edit3, Save, Check, X, Shield, Mail, AlertTriangle, Users, GraduationCap, CalendarClock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline editors used inside the licences table:
 *   - InlineOwnerEdit   : double-click holder_name to rename + PATCH
 *   - InlineMaxIpsEdit  : edit the IP allow-list cap
 *   - IpBadge           : visualise unique IPs vs cap (incl. tooltip)
 *   - InlineMasterEdit  : reassign an apprentice to another supervisor
 *
 * Extracted from AdminPanel.jsx on 28/05/2026. Each component owns its
 * own optimistic UI state and calls back to the parent through
 * onSaved() / onChanged() once the API roundtrip succeeds.
 */
export function InlineOwnerEdit({ licenseId, currentOwner, token, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentOwner || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/licenses/${licenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ owner: value }),
      });
      onSaved();
    } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input value={value}
          onChange={e => setValue(e.target.value)}
          className="bg-background text-foreground border border-accent/60 rounded px-2 py-1 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-accent"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          data-testid={`owner-input-${licenseId}`}
        />
        <button aria-label="Enregistrer" onClick={save} disabled={saving} className="p-1 text-money hover:bg-money/10 rounded" data-testid={`owner-save-${licenseId}`}>
          <Save className="w-3.5 h-3.5" />
        </button>
        <button aria-label="Annuler" onClick={() => setEditing(false)} className="p-1 text-slate-500 hover:bg-ink-700/60 rounded">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 group">
      <span className={`text-xs ${currentOwner ? 'text-slate-300' : 'text-slate-600 italic'}`}>
        {currentOwner || '—'}
      </span>
      <button aria-label="Modifier" onClick={() => setEditing(true)} className="p-0.5 text-slate-600 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`owner-edit-${licenseId}`}>
        <Edit3 className="w-3 h-3" />
      </button>
    </div>
  );
}

export function InlineMaxIpsEdit({ licenseId, currentMaxIps, token, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentMaxIps || 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/licenses/${licenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ max_ips: parseInt(value) || 0 }),
      });
      onSaved();
    } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input type="number" min={0}
          value={value}
          onChange={e => setValue(e.target.value)}
          className="bg-background text-foreground border border-accent/60 rounded px-2 py-1 text-xs w-16 focus:outline-none focus:ring-1 focus:ring-accent"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          data-testid={`maxips-input-${licenseId}`}
        />
        <button aria-label="Enregistrer" onClick={save} disabled={saving} className="p-1 text-money hover:bg-money/10 rounded">
          <Save className="w-3.5 h-3.5" />
        </button>
        <button aria-label="Annuler" onClick={() => setEditing(false)} className="p-1 text-slate-500 hover:bg-ink-700/60 rounded">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 group">
      <span className="text-xs text-slate-400">{currentMaxIps || '∞'}</span>
      <button aria-label="Modifier" onClick={() => setEditing(true)} className="p-0.5 text-slate-600 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
        <Edit3 className="w-3 h-3" />
      </button>
    </div>
  );
}

export function IpBadge({ uniqueIps, maxIps, licenseId }) {
  const [showIps, setShowIps] = useState(false);
  const count = (uniqueIps || []).length;
  const limited = maxIps > 0;
  const atLimit = limited && count >= maxIps;

  return (
    <div className="relative">
      <button
        onClick={() => setShowIps(!showIps)}
        className={`text-xs px-2 py-1 rounded-lg font-medium transition-colors ${
          atLimit ? 'bg-danger/10 text-danger hover:bg-danger/15' :
          count > 0 ? 'bg-accent/10 text-accent hover:bg-accent/15' :
          'bg-ink-700/30 text-slate-500 hover:bg-ink-700/60'
        }`}
        data-testid={`ip-badge-${licenseId}`}
      >
        {count} IP{count !== 1 ? 's' : ''}{limited ? ` / ${maxIps}` : ''}
      </button>
      {showIps && uniqueIps && uniqueIps.length > 0 && (
        <div className="absolute z-20 top-8 left-0 bg-ink-800 border border-ink-600 rounded-lg p-3 min-w-[180px]" data-testid={`ip-list-${licenseId}`}>
          <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Adresses IP</p>
          {uniqueIps.map((ip) => (
            <div key={ip} className="flex items-center gap-2 text-xs text-slate-300 py-1 border-b border-gray-50 last:border-0 font-mono">
              <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
              {ip}
            </div>
          ))}
          <button onClick={() => setShowIps(false)} className="mt-2 text-[10px] text-slate-500 hover:text-slate-400">Fermer</button>
        </div>
      )}
    </div>
  );
}

export function InlineMasterEdit({ licenseId, currentMasterId, masterLicenses, token, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentMasterId || '');
  const [saving, setSaving] = useState(false);

  const currentMaster = (masterLicenses || []).find(m => m.id === currentMasterId);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/licenses/${licenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ master_license_id: value || null }),
      });
      onSaved();
    } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <select
          value={value}
          onChange={e => setValue(e.target.value)}
          className="bg-background text-foreground border border-purple-400/60 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-400 max-w-[140px]"
          autoFocus
          data-testid={`master-select-${licenseId}`}
        >
          <option value="">— Aucune cohorte —</option>
          {(masterLicenses || []).map(m => (
            <option key={m.id} value={m.id}>{m.holder_name}</option>
          ))}
        </select>
        <button aria-label="Enregistrer" onClick={save} disabled={saving} className="p-1 text-money hover:bg-money/10 rounded" data-testid={`master-save-${licenseId}`}>
          <Save className="w-3.5 h-3.5" />
        </button>
        <button aria-label="Annuler" onClick={() => setEditing(false)} className="p-1 text-slate-500 hover:bg-ink-700/60 rounded">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 group">
      {currentMaster ? (
        <>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-medium" data-testid={`master-badge-${licenseId}`}>
            <GraduationCap className="w-3 h-3 inline mr-0.5" />
            {currentMaster.holder_name}
          </span>
          <button aria-label="Détacher de la cohorte"
            onClick={async () => {
              if (!window.confirm(`Détacher cette licence de la cohorte « ${currentMaster.holder_name} » ?`)) return;
              await fetch(`${API}/api/licenses/${licenseId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ master_license_id: null }),
              });
              onSaved();
            }}
            className="p-0.5 text-warn opacity-0 group-hover:opacity-100 transition-opacity hover:bg-warn/10 rounded"
            title="Détacher de la cohorte"
            data-testid={`master-detach-${licenseId}`}
          >
            <X className="w-3 h-3" />
          </button>
        </>
      ) : (
        <span className="text-[10px] text-slate-600 italic">—</span>
      )}
      <button aria-label="Modifier" onClick={() => setEditing(true)} className="p-0.5 text-slate-600 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`master-edit-${licenseId}`}>
        <Edit3 className="w-3 h-3" />
      </button>
    </div>
  );
}

/**
 * Edit a license's expiration date inline (admin Licences table → colonne
 * Expiration). Pre-fills with the current expires_at value and offers three
 * quick-add chips (+30 j / +90 j / +1 an) computed from the current expiry
 * (or from "today" if the license is already expired or has no date).
 *
 * 02/06/2026 — Added because the admin had no way to extend an existing
 * license without re-creating it. Backend already supports
 * `PUT /api/licenses/{id}` with body { expires_at: ISO string }.
 */
export function InlineExpiresEdit({ licenseId, currentExpiresAt, token, onSaved }) {
  // Helper : turn a Date into the YYYY-MM-DD string expected by <input type=date>.
  const toInputDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const currentDateObj = currentExpiresAt ? new Date(currentExpiresAt) : null;
  const initialValue = currentDateObj && !isNaN(currentDateObj.getTime())
    ? toInputDate(currentDateObj)
    : '';

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Quick add helper — N days from the current input value (or from today
  // if no value or already expired). Returns the YYYY-MM-DD string.
  const addDays = (days) => {
    const base = value
      ? new Date(`${value}T23:59:59`)
      : new Date();
    const today = new Date();
    // If the current value is already in the past, anchor on today so a
    // "+30j" never resolves to a date that's still expired.
    const start = base.getTime() < today.getTime() ? today : base;
    const next = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
    return toInputDate(next);
  };

  const save = async () => {
    setError(null);
    if (!value) {
      setError('Date requise');
      return;
    }
    // Send an ISO datetime at end-of-day UTC so the backend's date-parsing
    // logic (datetime.fromisoformat) treats the license as valid through
    // the entire chosen day (consistent with how validate_license compares
    // strict UTC > expires_at).
    const iso = new Date(`${value}T23:59:59Z`).toISOString();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/licenses/${licenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ expires_at: iso }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      if (typeof onSaved === 'function') onSaved();
      setEditing(false);
    } catch (e) {
      setError(String(e?.message || e).slice(0, 80));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1" data-testid={`expires-editor-${licenseId}`}>
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="bg-background text-foreground border border-accent/60 rounded px-2 py-1 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-accent"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
            data-testid={`expires-input-${licenseId}`}
          />
          <button aria-label="Enregistrer"
            onClick={save}
            disabled={saving}
            className="p-1 text-money hover:bg-money/10 rounded disabled:opacity-40"
            data-testid={`expires-save-${licenseId}`}
            title="Enregistrer"
          >
            <Save className="w-3.5 h-3.5" />
          </button>
          <button aria-label="Annuler"
            onClick={() => { setEditing(false); setError(null); setValue(initialValue); }}
            className="p-1 text-slate-500 hover:bg-ink-700/60 rounded"
            data-testid={`expires-cancel-${licenseId}`}
            title="Annuler"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-1" data-testid={`expires-presets-${licenseId}`}>
          {[
            { label: '+30 j', days: 30 },
            { label: '+90 j', days: 90 },
            { label: '+1 an', days: 365 },
          ].map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => setValue(addDays(p.days))}
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
              data-testid={`expires-preset-${p.days}-${licenseId}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {error && (
          <p className="text-[10px] text-danger" data-testid={`expires-error-${licenseId}`}>{error}</p>
        )}
      </div>
    );
  }

  const expired = currentDateObj && !isNaN(currentDateObj.getTime()) && currentDateObj < new Date();
  return (
    <div className="flex items-center gap-1 group">
      <span className={`text-xs ${expired ? 'text-danger' : 'text-slate-400'}`}>
        {currentDateObj && !isNaN(currentDateObj.getTime())
          ? currentDateObj.toLocaleDateString('fr-FR')
          : '—'}
      </span>
      <button aria-label="Modifier l'échéance"
        onClick={() => setEditing(true)}
        className="p-0.5 text-slate-600 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`expires-edit-${licenseId}`}
        title="Modifier l'échéance"
      >
        <CalendarClock className="w-3 h-3" />
      </button>
    </div>
  );
}

export function InlineCoSupervisorsEdit({ licenseId, current, masters, token, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(current || []);
  const [saving, setSaving] = useState(false);
  const others = (masters || []).filter((m) => m.id !== licenseId);
  const names = (current || [])
    .map((id) => others.find((m) => m.id === id)?.holder_name)
    .filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/licenses/${licenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ co_supervisors: selected }),
      });
      if (typeof onSaved === 'function') onSaved();
    } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="mt-1.5 bg-background border border-purple-400/40 rounded-lg p-2 w-56" data-testid={`cosup-editor-${licenseId}`}>
        <p className="text-[10px] uppercase tracking-wider text-purple-300 font-bold mb-1.5">Co-responsables de la cohorte</p>
        <div className="max-h-36 overflow-y-auto space-y-1">
          {others.length === 0 && <p className="text-[10px] text-slate-500 italic">Aucun autre responsable disponible.</p>}
          {others.map((m) => (
            <label key={m.id} className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-300 hover:text-foreground">
              <input
                type="checkbox"
                checked={selected.includes(m.id)}
                onChange={(e) => setSelected((prev) => (e.target.checked ? [...prev, m.id] : prev.filter((x) => x !== m.id)))}
                className="w-3 h-3"
                data-testid={`cosup-check-${licenseId}-${m.id}`}
              />
              <span className="truncate">{m.holder_name}{m.establishment ? ` — ${m.establishment}` : ''}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-1 mt-2">
          <button aria-label="Annuler" onClick={() => setEditing(false)} className="p-1 text-slate-500 hover:bg-ink-700/60 rounded" title="Annuler">
            <X className="w-3 h-3" />
          </button>
          <button aria-label="Enregistrer" onClick={save} disabled={saving} className="p-1 text-money hover:bg-money/10 rounded" title="Enregistrer" data-testid={`cosup-save-${licenseId}`}>
            <Save className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setSelected(current || []); setEditing(true); }}
      className={`mt-1 flex items-center gap-1 text-[10px] transition-colors ${
        names.length ? 'text-purple-300 hover:text-purple-200' : 'text-slate-600 hover:text-slate-400'
      }`}
      title="Gérer les co-responsables de cette cohorte"
      data-testid={`cosup-edit-${licenseId}`}
    >
      <Users className="w-3 h-3" />
      {names.length
        ? `+${names.length} co-resp. : ${names.slice(0, 2).join(', ')}${names.length > 2 ? '…' : ''}`
        : 'Ajouter des co-responsables'}
    </button>
  );
}

export function InlineStartsEdit({ licenseId, currentStartsAt, token, onSaved }) {
  const toLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(toLocal(currentStartsAt));
  const [saving, setSaving] = useState(false);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await fetch(`${API}/api/licenses/${licenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ starts_at: clear || !value ? null : new Date(value).toISOString() }),
      });
      if (typeof onSaved === 'function') onSaved();
    } catch {}
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 mt-1" data-testid={`starts-editor-${licenseId}`}>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="bg-background text-foreground border border-accent/60 rounded px-2 py-1 text-[10px] w-44 focus:outline-none focus:ring-1 focus:ring-accent"
          autoFocus
          data-testid={`starts-input-${licenseId}`}
        />
        <button aria-label="Enregistrer" onClick={() => save(false)} disabled={saving} className="p-1 text-money hover:bg-money/10 rounded" title="Enregistrer" data-testid={`starts-save-${licenseId}`}>
          <Save className="w-3 h-3" />
        </button>
        {currentStartsAt && (
          <button aria-label="Retirer la date de début" onClick={() => save(true)} disabled={saving} className="p-1 text-danger hover:bg-danger/10 rounded" title="Retirer la date de début" data-testid={`starts-clear-${licenseId}`}>
            <X className="w-3 h-3" />
          </button>
        )}
        <button aria-label="Annuler" onClick={() => setEditing(false)} className="p-1 text-slate-500 hover:bg-ink-700/60 rounded" title="Annuler">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  const future = currentStartsAt && new Date(currentStartsAt) > new Date();
  return (
    <div className="flex items-center gap-1 group/starts mt-0.5">
      {future ? (
        <span className="text-[10px] text-warn font-medium" data-testid={`starts-badge-${licenseId}`}>
          ⏳ Ouvre le {new Date(currentStartsAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </span>
      ) : (
        <span className="text-[10px] text-slate-600 italic opacity-0 group-hover/starts:opacity-100 transition-opacity">Programmer l'ouverture</span>
      )}
      <button aria-label="Programmer la date d'ouverture (salle d'attente)"
        onClick={() => { setValue(toLocal(currentStartsAt)); setEditing(true); }}
        className="p-0.5 text-slate-600 hover:text-foreground opacity-0 group-hover/starts:opacity-100 transition-opacity"
        data-testid={`starts-edit-${licenseId}`}
        title="Programmer la date d'ouverture (salle d'attente)"
      >
        <CalendarClock className="w-3 h-3" />
      </button>
    </div>
  );
}
